/**
 * Client for the Ask Tripsby host assistant (Phase 1: read-only).
 *
 * POST {base}/host-assistant/stream answers with a Server-Sent-Events body:
 *   data: {"type":"status","message":"..."}                (zero or more)
 *   data: {"type":"complete","result":{response,facts?}}   -- or --
 *   data: {"type":"error","code":"...","message":"..."}
 * Failures that happen before the stream opens (401/403/404/400/429/503) come
 * back as plain JSON instead.
 *
 * Deliberately not axios: axios buffers the whole body in the browser, and the
 * point of the stream is the live status line. Dependency-free on purpose.
 *
 * Base URL: reuses the Concierge Builder API URL (the assistant lives in Studio's
 * own backend), unless VITE_HOST_ASSISTANT_BASE_URL overrides it.
 */

import { CONCIERGE_API_URL } from './concierge-api-client';
import { clearAuthToken, getAuthToken } from '../lib/auth/token';

export const HOST_ASSISTANT_BASE_URL = (
  import.meta.env.VITE_HOST_ASSISTANT_BASE_URL || CONCIERGE_API_URL
).replace(/\/$/, '');

export const HOST_ASSISTANT_TIMEOUT_MS = 120_000;

export interface HostAssistantTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface HostAssistantRequest {
  group_id: string;
  message: string;
  history?: HostAssistantTurn[];
  tab?: string;
}

export interface HostAssistantFact {
  label: string;
  value: string;
}

export interface HostAssistantResult {
  response: string;
  facts?: HostAssistantFact[];
}

/**
 * Codes the backend documents, plus the ones only this client can produce
 * (`network`, `timeout`, `aborted`, `stream_incomplete`, `unavailable`).
 */
export type HostAssistantErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'group_not_found'
  | 'invalid_request'
  | 'rate_limited'
  | 'host_assistant_unconfigured'
  | 'unavailable'
  | 'network'
  | 'timeout'
  | 'aborted'
  | 'stream_incomplete'
  | (string & {});

export class HostAssistantError extends Error {
  readonly code: HostAssistantErrorCode;
  readonly status?: number;

  constructor(code: HostAssistantErrorCode, message: string, status?: number) {
    super(message);
    this.name = 'HostAssistantError';
    this.code = code;
    this.status = status;
  }
}

export interface StreamOptions {
  onStatus?: (message: string) => void;
  signal?: AbortSignal;
  timeoutMs?: number;
}

// ---------------------------------------------------------------------------
// SSE framing
// ---------------------------------------------------------------------------

export interface SseFrameParser {
  /** Feed a decoded text chunk. Emits the `data` payload of every complete frame. */
  push(chunk: string): void;
  /** End of stream: emits a final frame that was never terminated by a blank line. */
  flush(): void;
}

/**
 * Incremental SSE parser. A frame ends at a blank line; a network chunk can end
 * anywhere, mid-line or mid-JSON, so the unfinished tail is buffered until the
 * next chunk completes it. Multi-line `data:` fields are joined with "\n" per the
 * SSE spec; comments (":") and other fields (event/id/retry) are ignored.
 */
export function createSseFrameParser(onData: (data: string) => void): SseFrameParser {
  let buffer = '';

  const emit = (frame: string) => {
    const data: string[] = [];
    for (const line of frame.split('\n')) {
      if (!line.startsWith('data:')) continue;
      const value = line.slice(5);
      data.push(value.startsWith(' ') ? value.slice(1) : value);
    }
    if (data.length) onData(data.join('\n'));
  };

  return {
    push(chunk) {
      buffer += chunk.replace(/\r\n?/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        emit(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');
      }
    },
    flush() {
      if (buffer.trim()) emit(buffer);
      buffer = '';
    },
  };
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

const STATUS_FALLBACK: Record<number, HostAssistantErrorCode> = {
  400: 'invalid_request',
  401: 'unauthorized',
  403: 'forbidden',
  429: 'rate_limited',
  // A missing route is what an undeployed backend looks like; present it as
  // "not set up yet" rather than as a broken group.
  404: 'host_assistant_unconfigured',
  405: 'host_assistant_unconfigured',
};

async function preStreamError(response: Response): Promise<HostAssistantError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    /* not JSON (proxy page, empty body) — fall back to the status */
  }
  const record = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const detail = (record.detail && typeof record.detail === 'object' ? record.detail : {}) as Record<string, unknown>;
  const bodyCode = [record.code, detail.code].find((c) => typeof c === 'string' && c) as string | undefined;
  // The documented 404 is group_not_found; only a body-less 404 means "no route".
  const code = bodyCode ?? STATUS_FALLBACK[response.status] ?? 'unavailable';
  const message = [record.message, detail.message, record.detail].find((m) => typeof m === 'string' && m) as
    | string
    | undefined;
  return new HostAssistantError(code, message ?? `Ask Tripsby request failed (${response.status})`, response.status);
}

function handleUnauthorized() {
  // Same recovery as groups-api-client: a 401 is the server's authoritative
  // answer, so drop the local session and send the host to sign in again.
  try {
    clearAuthToken();
  } catch {
    /* storage unavailable — redirect anyway */
  }
  if (typeof window !== 'undefined' && !/\/login/.test(window.location.pathname)) {
    window.location.href = '/login';
  }
}

// ---------------------------------------------------------------------------
// The call
// ---------------------------------------------------------------------------

type Frame = { type?: unknown; message?: unknown; code?: unknown; result?: unknown };

function asResult(raw: unknown): HostAssistantResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { response?: unknown; facts?: unknown };
  if (typeof r.response !== 'string') return null;
  const facts = Array.isArray(r.facts)
    ? r.facts
        .filter((f): f is { label: unknown; value: unknown } => !!f && typeof f === 'object')
        .map((f) => ({ label: String(f.label ?? ''), value: String(f.value ?? '') }))
        .filter((f) => f.label || f.value)
    : undefined;
  return { response: r.response, ...(facts && facts.length ? { facts } : {}) };
}

export async function streamHostAssistant(
  request: HostAssistantRequest,
  { onStatus, signal, timeoutMs = HOST_ASSISTANT_TIMEOUT_MS }: StreamOptions = {},
): Promise<HostAssistantResult> {
  if (signal?.aborted) throw new HostAssistantError('aborted', 'Request cancelled');

  // One controller for both the caller's Stop and our own timeout.
  const controller = new AbortController();
  let timedOut = false;
  const onCallerAbort = () => controller.abort();
  signal?.addEventListener('abort', onCallerAbort);
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const interrupted = (): HostAssistantError =>
    timedOut
      ? new HostAssistantError('timeout', 'Ask Tripsby took too long to answer.')
      : new HostAssistantError('aborted', 'Request cancelled');

  try {
    const token = getAuthToken();
    let response: Response;
    try {
      response = await fetch(`${HOST_ASSISTANT_BASE_URL}/host-assistant/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      });
    } catch (err) {
      if (controller.signal.aborted) throw interrupted();
      throw new HostAssistantError('network', err instanceof Error ? err.message : 'Network error');
    }

    if (!response.ok) {
      const error = await preStreamError(response);
      if (error.code === 'unauthorized') handleUnauthorized();
      throw error;
    }
    if (!response.body) throw new HostAssistantError('stream_incomplete', 'The response had no body.');

    let result: HostAssistantResult | null = null;
    let failure: HostAssistantError | null = null;
    const parser = createSseFrameParser((data) => {
      if (result || failure) return;
      let frame: Frame;
      try {
        frame = JSON.parse(data) as Frame;
      } catch {
        return; // keep-alive or non-JSON payload
      }
      if (frame.type === 'status' && typeof frame.message === 'string') {
        onStatus?.(frame.message);
      } else if (frame.type === 'complete') {
        result = asResult(frame.result);
        if (!result) failure = new HostAssistantError('stream_incomplete', 'The answer was malformed.');
      } else if (frame.type === 'error') {
        failure = new HostAssistantError(
          typeof frame.code === 'string' && frame.code ? frame.code : 'unavailable',
          typeof frame.message === 'string' && frame.message ? frame.message : 'Ask Tripsby hit an error.',
        );
      }
    });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    try {
      while (!result && !failure) {
        const { done, value } = await reader.read();
        if (done) break;
        parser.push(decoder.decode(value, { stream: true }));
      }
      if (!result && !failure) {
        parser.push(decoder.decode());
        parser.flush();
      }
    } catch (err) {
      if (controller.signal.aborted) throw interrupted();
      throw new HostAssistantError('network', err instanceof Error ? err.message : 'The connection dropped.');
    } finally {
      reader.cancel().catch(() => undefined);
    }

    if (failure) throw failure;
    if (result) return result;
    throw new HostAssistantError('stream_incomplete', 'The connection closed before the answer finished.');
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onCallerAbort);
  }
}

// ---------------------------------------------------------------------------
// Warm-up
// ---------------------------------------------------------------------------

let warmRequested = false;

/**
 * Ask the backend to get the assistant ready (POST /host-assistant/warm, signed in, 204).
 *
 * The first question after a scale-to-zero otherwise pays ~25 s of client start-up. Called when
 * the Ask Tripsby panel opens, or when the pointer or keyboard reaches its launcher, so that
 * wait overlaps the host reading the panel instead of following their first question.
 *
 * Fire-and-forget and best-effort by design: it returns nothing, never throws, shows nothing,
 * and at most ONE request goes out per page session. A failure is not retried: the stream
 * endpoint initialises on demand regardless, so a missed warm-up only costs the cold start it
 * was meant to hide. A 401 is deliberately not treated as a sign-out here (the stream call
 * does that when the host actually asks something).
 */
export function warmHostAssistant(): void {
  if (warmRequested) return;
  try {
    const token = getAuthToken();
    if (!token) return; // not signed in: the endpoint would only answer 401 (and a later call may have a token)
    warmRequested = true;
    void fetch(`${HOST_ASSISTANT_BASE_URL}/host-assistant/warm`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => undefined);
  } catch {
    /* storage or fetch unavailable: the first question simply pays the cold start */
  }
}

/** Test seam: forget that a warm-up was requested. Not for application code. */
export function resetHostAssistantWarmForTests(): void {
  warmRequested = false;
}
